import clsx from 'clsx';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@shared/components/Icon';
import { AiSummaryButton, AiSummaryResult } from '@shared/scenes/ask/components/AiSummary';
import { useAiSummariesEnabled } from '@shared/scenes/ask/useAiSummariesEnabled';
import { isAiSummaryButtonShown, useAiSummary } from '@shared/scenes/ask/useAiSummary';
import { FormSearchContextValue } from '@shared/scenes/entity/context';
import { getComponentLabelWithFallback, isSubformComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { FIELD_SEARCH, useFeatureFlag } from '@shared/util/featureFlags';
import { Input } from '@ui/Input';
import { whiteInputStyles } from '@ui/Input/styles';
import { buildCardSummaryPrompt } from './buildCardSummaryPrompt';
import { useCardSummary } from './useCardSummary';
import styles from './FormToolbar.module.css';

const searchInputWrapper = `${whiteInputStyles.inputWrapper} h-10 min-h-10 shadow-none`;

interface Props {
  /** The form's field search; without a provider above the form, there is no search. */
  search: FormSearchContextValue | null;
  /** The card (workflow-entity) id — the Ask scope the summary is resolved against. */
  entityId?: string;
  /** The card's own workflow template id, so the prompt can distinguish it from its subforms. */
  workflowTemplateId: string;
  /** All components on the card; subform components are surfaced to the prompt by name and template. */
  components: API.AnyComponent[];
  /** False on a public form, which gets the field search but not the summary. */
  isAuthenticated: boolean;
}

/**
 * Tools for the form as a whole, above its fields and divided from them: the field search, and
 * the "Summarize" button once the card exists. The row stays pinned to the top of the form's
 * scroller; the summary it generates shows below the row and scrolls away with the fields.
 */
export function FormToolbar({
  search,
  entityId,
  workflowTemplateId,
  components,
  isAuthenticated,
}: Props) {
  const { t } = useTranslation();
  const isSearchFlagEnabled = useFeatureFlag(FIELD_SEARCH);
  const isSummaryEnabled = useAiSummariesEnabled();
  const cardSummary = useCardSummary();

  const buildPrompt = useCallback(() => {
    const subforms = components.filter(isSubformComponent).map(component => ({
      name: getComponentLabelWithFallback(component),
      workflowTemplateIds: component.workflowTemplateIds ?? [],
    }));
    return buildCardSummaryPrompt({ workflowTemplateId, subforms });
  }, [workflowTemplateId, components]);

  const onSummaryGenerated = useCallback(
    (summary: string) => cardSummary?.setSummary(summary),
    [cardSummary],
  );
  const { status, summarize } = useAiSummary({
    scope: { type: 'card', id: entityId ?? '' },
    buildPrompt,
    onSummaryGenerated,
  });

  // The summary needs a signed-in user, a saved card and a card-summary provider (i.e. the
  // editable card view).
  const summary =
    isAuthenticated && isSummaryEnabled && !isNil(cardSummary) && !isNil(entityId)
      ? cardSummary
      : null;

  const isSummaryButtonShown = !isNil(summary) && isAiSummaryButtonShown(summary.summary, status);
  const isSearchEnabled = isSearchFlagEnabled && !isNil(search);
  const isRowShown = isSearchEnabled || isSummaryButtonShown;

  if (!isSearchEnabled && isNil(summary)) {
    return null;
  }

  return (
    <>
      {isRowShown && (
        <div className={styles.toolbar}>
          <div className={styles.toolbarRow}>
            {isSearchEnabled && (
              <div className={styles.toolbarSearch}>
                <Input
                  value={search.query}
                  onValueChange={search.setQuery}
                  placeholder={t('b.viewTemplates.settings.searchComponents')}
                  size="sm"
                  rounded="full"
                  isClearable
                  classNames={{ inputWrapper: searchInputWrapper }}
                  startContent={
                    <Icon name="magnifying-search-01" className="text-gray-30" gridSize={3} />
                  }
                />
              </div>
            )}
            {isSummaryButtonShown && (
              <AiSummaryButton onClick={summarize} testIDPrefix="card" size="medium" />
            )}
          </div>
        </div>
      )}
      {!isNil(summary) && (
        <div className={clsx(styles.summary, !isRowShown && styles.withoutRow)}>
          <div className={styles.summaryContent}>
            <AiSummaryResult summary={summary.summary} status={status} />
          </div>
        </div>
      )}
    </>
  );
}
